import { useLoaderData, LoaderFunctionArgs } from "react-router-dom";
import { PROMO_BALANCE_REPORT } from "./fragment";
import { colDefs, formatData } from "./helpers";
import { ReportFilters } from "../../components/ReportFilters";
import ReportHeader from "../../components/ReportHeader";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../../store/organization";
import { userStore } from "../../../../store/user";
import { PromoBalanceReportRow } from "./type";
import { useRef } from "react";
import { eventStore } from "../../../../store/event";
import { GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { SecondaryButton } from "../../../../components/Button";
import { AgGridReact } from "ag-grid-react";

const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;

export const promoBalanceReportLoader = async ({
  request,
}: LoaderFunctionArgs) => {
  const rawOrganizationId = getOrganizationState().organizationId;
  const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
  const { eventId } = getEventState();
  const user = getState().user;
  const permissions = getState().permissions;

  const search = new URL(request.url);
  const where = search.searchParams.get("where");

  const vendorWhere =
    permissions === "vendor"
      ? {
          _and: {
            vendor_id: {
              _in: user?.vendors,
            },
          },
        }
      : {};

  let whereVariable: any = where
    ? {
        ...JSON.parse(where),
        ...vendorWhere,
      }
    : {};

  if (organizationId && organizationId !== 0) {
    whereVariable = {
      ...whereVariable,
      ...vendorWhere,
      organization_id: {
        _eq: organizationId,
      },
    };
  }

  if (eventId) {
    whereVariable = {
      ...whereVariable,
      ...vendorWhere,
      event_id: {
        _eq: eventId,
      },
    };
  } else if (user?.events) {
    whereVariable = {
      ...whereVariable,
      ...vendorWhere,
      event_id: {
        _in: user?.events,
      },
    };
  }

  const { data } = await client.query({
    query: PROMO_BALANCE_REPORT,
    variables: { where: whereVariable },
  });
  return data.reports_promo_balance || [];
};

export const PromoBalanceReport = () => {
  const data = useLoaderData() as PromoBalanceReportRow[];
  const rows = formatData(data);
  const tableRows = rows.filter((row) => row.username !== "Total");
  const initialUsernamesRef = useRef<{ label: string; value: string }[] | null>(null);
  if (!initialUsernamesRef.current) {
    initialUsernamesRef.current = [
      ...new Set(data.map((row) => row.username))
    ].map((username) => ({
      label: username,
      value: username
    }));
  }
  const totalRow = rows.filter((row) => row.username === "Total");
  const gridRef = useRef<GridApi>();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `PromoBalanceLoadedReport_${timestamp}.csv`, // Updated file name with timestamp
        columnSeparator: ",",
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  return (
    <>
      <div className="bg-white px-4 pb-1 mb-3 rounded-md">
        <ReportHeader title="Promo Balance Loaded Report" />
        <ReportFilters
          includeDates
          includeTime
          includeUsernames
          includeUsername={initialUsernamesRef.current}
          includeLocations
          customQueryPaths={{
            dateFrom: "_and[0].created_at_hour",
            dateTo: "_and[1].created_at_hour",
          }}
        />
        <div className="flex">
          <div className="ml-auto">
            <SecondaryButton onClick={handleExportCSV}>
              Export to CSV
            </SecondaryButton>
          </div>
        </div>
      </div>
      <div style={{ height: "60vh", marginTop: "4px" }}>
        <div
          className={"ag-theme-quartz"}
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact
            rowData={tableRows}
            columnDefs={colDefs}
            rowHeight={rowHeight}
            autoSizeStrategy={autoSizeStrategy}
            pagination={true}
            paginationPageSize={100}
            gridOptions={gridOptions}
            pinnedBottomRowData={totalRow}
            onGridReady={(params) => (gridRef.current = params.api)} // Set GridApi reference
          />
        </div>
      </div>
    </>
  );
};
